<template>
    <div class="bg-main-inner-bg p-3 rounded-3">
        <div class="row">
            <div class="col-lg-6">
                <p class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-3">
                    {{ __('settings_section.address.available_elements') }}
                </p>
                <draggable class="address-list" :list="availableElements"
                    :group="{ name: 'address-elements', pull: 'clone', put: false }" :sort="false"
                    :clone="cloneElement">
                    <span v-for="element in availableElements" :key="`available-${element.id}`" class="address-chip">
                        {{ element.label }}
                    </span>
                </draggable>
            </div>

            <div class="col-lg-6 mt-3 mt-lg-0">
                <p class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-3">
                    {{ __('settings_section.address.address_composition') }}
                </p>
                <draggable class="address-list" v-model="localValue"
                    :group="{ name: 'address-elements', pull: true, put: true }" @change="emitChange">
                    <span v-for="(element, index) in localValue" :key="`selected-${element.id}-${index}`"
                        class="address-chip selected">
                        {{ element.label }}
                        <button class="remove-chip" type="button" @click.stop="removeElement(index)">
                            <i class="fa-solid fa-xmark"></i>
                        </button>
                    </span>
                </draggable>
                <small class="text-muted d-block mt-2">
                    {{ __('settings_section.address.drag_and_reorder_items') }}
                </small>
            </div>
        </div>

        <div class="mt-4">
            <p class="font-bold text-smaller border-bottom border-dark-subtle pb-2 mb-3">
                {{ __('settings_section.address.preview') }}
            </p>

            <div class="row">
                <div class="col-lg-6">
                    <l-map class="preview-map" :zoom="previewMap.zoom" :center="previewMap.center" @click="onMapClick">
                        <l-tile-layer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            :attribution="'&copy; OpenStreetMap contributors'" />
                        <l-circle-marker v-if="previewMap.marker" :lat-lng="previewMap.marker" :radius="5"
                            :color="'#d71438'" :fill-color="'#d71438'" :fill-opacity="0.9" :weight="2" />
                    </l-map>
                    <small class="text-muted d-block mt-2">
                        {{ __('settings_section.address.click_to_fetch') }}
                    </small>
                </div>

                <div class="col-lg-6 mt-3 mt-lg-0">
                    <small class="text-muted d-block mb-2">
                        {{ __('settings_section.address.info_preview') }}
                    </small>
                    <div class="preview-box mb-2">
                        <span v-if="localValue.length === 0"> - </span>
                        <span v-else class="template-chips">
                            <span v-for="(item, index) in localValue" :key="`tpl-${item.id}-${index}`"
                                class="template-chip" :class="[
                                    { 'is-present': isFieldAvailable(item.id) },
                                    { 'is-not-present': !isFieldAvailable(item.id) },
                                ]" @mouseenter="hoveredField = item.id" @mouseleave="hoveredField = null">
                                {{ getElementLabel(item.id) }}
                            </span>
                        </span>
                    </div>
                    <div class="preview-box">
                        <span v-if="resolvedAddressParts.length === 0"> - </span>
                        <span v-else>
                            <span v-for="(part, index) in resolvedAddressParts" :key="`result-${part.field}-${index}`">
                                <span class="result-part" :class="{ 'is-highlighted': hoveredField === part.field }">{{
                                    part.value }}</span>
                                <span v-if="index < resolvedAddressParts.length - 1">, </span>
                            </span>
                        </span>
                    </div>
                    <small class="text-muted d-block mt-2">
                        <b>{{ __('settings_section.address.coordinates') }}</b> {{ clickedCoordinates || '-' }}
                    </small>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import draggable from 'vuedraggable'
    import { LMap, LTileLayer, LCircleMarker } from 'vue2-leaflet'

    export default {
        name: 'AddressCompositionBuilder',
        components: {
            draggable,
            LMap,
            LTileLayer,
            LCircleMarker
        },
        props: {
            value: {
                type: Array,
                default: () => []
            },
            addressElements: {
                type: Array,
                default: () => []
            }
        },
        data() {
            return {
                localValue: [],
                previewMap: {
                    zoom: 10,
                    center: [45.4238702, 10.9002853],
                    marker: [45.4238702, 10.9002853]
                },
                reverseAddress: null,
                loadingPreview: false,
                hoveredField: null
            }
        },
        computed: {
            availableElements() {
                const selectedIds = this.localValue.map((item) => item.id)
                return this.addressElements
                    .filter((element) => !selectedIds.includes(element))
                    .map((element) => ({
                        id: element,
                        label: this.getElementLabel(element)
                    }))
            },
            previewText() {
                return this.localValue.map((item) => `{${item.id}}`).join(', ')
            },
            clickedCoordinates() {
                if (!this.previewMap.marker) return null
                return `${this.previewMap.marker[0].toFixed(6)}, ${this.previewMap.marker[1].toFixed(6)}`
            },
            resolvedAddressPreview() {
                if (this.loadingPreview) return 'Loading...'
                if (!this.reverseAddress) return null

                const parts = this.localValue.map((item) => this.reverseAddress[item.id]).filter((item) => !!item)
                return parts.join(', ')
            },
            resolvedAddressParts() {
                if (this.loadingPreview || !this.reverseAddress) return []
                return this.localValue
                    .map((item) => ({
                        field: item.id,
                        value:
                            item.id === 'country_code' && this.reverseAddress[item.id]
                                ? String(this.reverseAddress[item.id]).toUpperCase()
                                : this.reverseAddress[item.id]
                    }))
                    .filter((part) => !!part.value)
            }
        },
        watch: {
            value: {
                immediate: true,
                deep: true,
                handler(newValue) {
                    this.localValue = (newValue || []).map((item) => ({
                        id: item.id || item,
                        label: this.getElementLabel(item.id || item)
                    }))
                }
            }
        },
        methods: {
            getElementLabel(element) {
                const key = `settings_section.address.elements.${element}`
                const translated = this.__(key)
                return translated === key ? element : translated
            },
            cloneElement(element) {
                return {
                    id: element.id,
                    label: this.getElementLabel(element.id)
                }
            },
            removeElement(index) {
                this.localValue.splice(index, 1)
                this.emitChange()
            },
            isFieldAvailable(field) {
                if (!this.reverseAddress) return false
                return !!this.reverseAddress[field]
            },
            async onMapClick(event) {
                const { lat, lng } = event.latlng
                this.previewMap.marker = [lat, lng]
                this.previewMap.center = [lat, lng]
                this.loadingPreview = true

                try {
                    const address = await this.$reverseGeocoding({
                        lat,
                        lng,
                        raw_address_object: true
                    })
                    this.reverseAddress = address || null
                } catch (error) {
                    this.reverseAddress = null
                    console.error(error)
                } finally {
                    this.loadingPreview = false
                }
            },
            emitChange() {
                const unique = this.localValue.filter(
                    (item, index, self) => self.findIndex((i) => i.id === item.id) === index
                )
                this.localValue = unique
                this.$emit('input', unique)
                this.$emit(
                    'change',
                    unique.map((item) => item.id)
                )
            }
        }
        ,
        created() {
            if (this.previewMap.marker && this.previewMap.marker.length === 2) {
                this.onMapClick({
                    latlng: {
                        lat: this.previewMap.marker[0],
                        lng: this.previewMap.marker[1]
                    }
                })
            }
        }
    }
</script>

<style scoped lang="scss">
    .address-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
    }

    .address-chip {
        background: #e9edf3;
        border: 1px solid #cfd8e3;
        border-radius: 16px;
        padding: 2px 10px;
        font-size: 11px;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        cursor: grab;
    }

    .address-chip.selected {
        background: #d9e9ff;
        border-color: #9ec5ff;
    }

    .remove-chip {
        border: 0;
        background: transparent;
        padding: 0;
        line-height: 1;
        color: #333;
    }

    .preview-box {
        background: #fff;
        border: 1px solid #d2d2d2;
        border-radius: 10px;
        padding: 10px 12px;
        font-size: 11px;
    }

    .preview-map {
        height: 240px;
        border-radius: 10px;
        overflow: hidden;
    }

    .template-chips {
        display: inline-flex;
        flex-wrap: wrap;
        gap: 6px;
    }

    .template-chip {
        background: #f0f2f5;
        border-radius: 12px;
        padding: 4px 8px;
        font-size: 10px;
        line-height: 1;
        cursor: pointer;
    }

    .template-chip.is-present {
        background: #dff6e5;
        border-color: #79c592;
        color: #1f6f3c;

        &:hover {
            background: #c1e8c9;
        }
    }

    .template-chip.is-not-present {
        background: #f8d7da;
        border-color: #f5c6cb;
        color: #721c24;
    }

    .result-part.is-highlighted {
        color: #d71438;
    }

    small {
        font-size: 10px;

        b {
            font-weight: bolder;
            color: black;
        }
    }
</style>
